





















































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import Thumbnail from "./Thumbnail.vue";
import { Art, displayImageFor } from "@/models/Art";
import { storage } from "../firebaseInit";
import { getDownloadURL, ref } from "firebase/storage";

type Measurement = { url: string; ratio: number };

/* Must match the `gap` on .art-showcase below. */
const GAP = 24;

/*
  How many of the unrecorded stragglers are measured at once. Enough to keep
  the connection busy, few enough that the first row is not waiting behind the
  last one.
*/
const LANES = 4;

/*
  A justified wall, the way a picture hang actually works: every painting keeps
  its own proportions and the row is what gets squared off, not the artwork.

  The old layout forced a 41"x10" panel and a square canvas through the same
  32.1% column at every screen size. Here each piece is laid out with a
  flex-basis and flex-grow proportional to its aspect ratio, so when flexbox
  distributes the leftover width across a row, every item in that row lands on
  the same image height. Rows line up; nothing is cropped.

  Because those proportions come from the images themselves, the wall used to
  hang every painting at a guessed ratio and then rearrange itself underneath
  the reader as the real ones arrived. Now every painting carries its own
  proportions on its record, so the whole wall can be hung correctly before a
  single picture has been downloaded, and it never moves again.
*/
@Component({
  components: {
    Thumbnail,
  },
})
export default class ArtShowcase extends Vue {
  @Prop({ required: true }) readonly artArray!: Art[];

  /* Download URL and aspect ratio (width / height) per artwork, once measured. */
  private measured: { [id: string]: Measurement } = {};
  private rowHeight = 320;
  private containerWidth = 0;
  /* Invalidates in-flight measurements when the collection changes. */
  private run = 0;

  created() {
    this.setMetrics();
    window.addEventListener("resize", this.setMetrics);
  }

  mounted() {
    this.setMetrics();
    this.measureAll();
  }

  destroyed() {
    this.run++;
    window.removeEventListener("resize", this.setMetrics);
  }

  @Watch("artArray")
  onArtArrayChange() {
    this.measureAll();
  }

  /*
    Target row height, not a column count. A taller target on a phone means
    roughly one or two paintings per row instead of three postage stamps.
  */
  setMetrics() {
    const width = window.innerWidth;
    if (width < 560) this.rowHeight = 230;
    else if (width < 900) this.rowHeight = 270;
    else this.rowHeight = 340;

    const wall = this.$refs.wall as HTMLElement | undefined;
    if (wall) this.containerWidth = wall.clientWidth;
  }

  /*
    Hangs every painting whose shape is on record, then measures whatever is
    left over.

    A painting's display copy carries its pixel size on its Firestore document
    (see `displayImageFor`), and the address of that copy can be worked out from
    the painting rather than asked for. So the wall knows the shape of the whole
    collection the moment the collection arrives: no downloads, no round trips,
    no guessing. Every frame is laid out correctly on the first frame, and the
    browser fills them in as the pictures come down.

    Anything uploaded before display copies existed still has to be measured the
    slow way — fetch its URL, load the image off-DOM, read its natural size —
    and that runs a few at a time, in the order they hang. Firebase Storage
    speaks HTTP/2, so asking for all of them at once does not queue them: it
    multiplexes them down one connection where they share the bandwidth equally
    and every one finishes at the same late moment. A narrow window gives the
    top of the wall the whole pipe instead. Run
    `node scripts/build-display-images.mjs` and this path goes quiet.
  */
  async measureAll() {
    const run = ++this.run;
    const measured: { [id: string]: Measurement } = {};
    const unrecorded: Art[] = [];

    for (const piece of this.artArray) {
      const display = displayImageFor(piece);
      if (display) measured[piece.id] = display;
      else unrecorded.push(piece);
    }

    this.measured = measured;
    if (!unrecorded.length) return;

    let next = 0;

    const lane = async () => {
      for (let i = next++; i < unrecorded.length; i = next++) {
        if (run !== this.run) return;
        const piece = unrecorded[i];
        let result: Measurement = { url: "", ratio: 0 };
        try {
          const url = await getDownloadURL(ref(storage, piece.storageLocation));
          result = { url, ratio: await this.ratioOf(url) };
        } catch {
          /*
            A painting whose file is missing or unreadable is skipped rather
            than left to hold up every row behind it.
          */
        }
        if (run !== this.run) return;
        this.$set(this.measured, piece.id, result);
      }
    };

    await Promise.all(Array.from({ length: LANES }, lane));
  }

  ratioOf(url: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve(
          img.naturalWidth && img.naturalHeight
            ? img.naturalWidth / img.naturalHeight
            : 0
        );
      img.onerror = () => reject(new Error("image failed to load"));
      img.src = url;
    });
  }

  /*
    How many paintings can be hung without anything moving later: everything up
    to the end of the last row that can already be closed. Rows close the same
    way flexbox wraps them — by adding flex-basis and gaps until the next item
    would not fit.
  */
  get visibleCount() {
    const art = this.artArray;
    let known = 0;
    while (known < art.length && this.measured[art[known].id]) known++;

    /* Everything has arrived, so the last, unfilled row can be hung too. */
    if (known === art.length) return known;
    if (!this.containerWidth) return 0;

    let complete = 0;
    let rowRatio = 0;
    let rowItems = 0;
    for (let i = 0; i < known; i++) {
      const ratio = this.ratioFor(art[i].id);
      const width = (rowRatio + ratio) * this.rowHeight + GAP * rowItems;
      if (rowItems > 0 && width > this.containerWidth) {
        complete = i;
        rowRatio = ratio;
        rowItems = 1;
      } else {
        rowRatio += ratio;
        rowItems++;
      }
    }
    return complete;
  }

  get visibleArt() {
    return this.artArray
      .slice(0, this.visibleCount)
      .filter((art) => this.measured[art.id].ratio > 0);
  }

  get isComplete() {
    return this.artArray.every((art) => this.measured[art.id]);
  }

  /* Share of the collection that has been measured, 0 to 1. */
  get progress() {
    const total = this.artArray.length;
    if (!total) return 0;
    return this.artArray.filter((art) => this.measured[art.id]).length / total;
  }

  ratioFor(id: string) {
    const measurement = this.measured[id];
    const ratio = measurement && measurement.ratio;
    return ratio && isFinite(ratio) ? ratio : 1.3;
  }

  flexFor(art: Art) {
    const ratio = this.ratioFor(art.id);
    return {
      flexGrow: String(ratio),
      flexBasis: `${ratio * this.rowHeight}px`,
    };
  }
}
